import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip, Menu, MenuItem, Typography, Chip, Button } from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import { useThulium } from '../views/Overview/PhoneCallDialog/ThuliumProvider';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { communicationActions } from '../store/slices/communicationSlice';
import axios from 'src/utils/axios';

const useStyles = makeStyles((theme) => ({
  phoneContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    marginRight: theme.spacing(1),
  },
  phoneButton: {
    marginRight: theme.spacing(1),
    position: 'relative',
  },
  phoneLabel: {
    marginRight: theme.spacing(1),
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  statusDot: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
  },
  onlineDot: {
    backgroundColor: '#4caf50',
    animation: '$pulse 2s ease-in-out infinite',
  },
  offlineDot: {
    backgroundColor: '#f44336',
  },
  activeCallChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    animation: '$pulse 2s ease-in-out infinite',
    marginRight: theme.spacing(1),
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.7,
    },
  },
}));

const PhoneIntegration = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [SearchData, setSearchData] = useState([]);

  // Thulium provider'dan gerçek verileri al
  const {
    connectionStatus,
    activeContact
  } = useThulium();

  const isConnected = connectionStatus === 'connected';

  // Redux'tan activeCustomer'ı al
  const activeCustomer = useSelector((state) => state.communication.activeCustomer);

  // Call duration tracking
  useEffect(() => {
    let interval;
    if (activeContact && activeContact.startTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((new Date() - activeContact.startTime) / 1000);
        setCallDuration(elapsed);
      }, 1000);
    } else {
      setCallDuration(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeContact]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <>
      <div className={classes.phoneContainer}>
        <Typography className={classes.phoneLabel} color="inherit">
          Phone Integration
        </Typography>
        <Tooltip title={isConnected ? 'Thulium: Connected' : connectionStatus === 'connecting' ? 'Thulium: Connecting...' : 'Thulium: Disconnected'}>
          <IconButton
            className={classes.phoneButton}
            onClick={handleClick}
            color="inherit"
            size="small"
          >
            {activeContact ? <PhoneInTalkIcon /> : <PhoneIcon />}
            <span className={`${classes.statusDot} ${isConnected ? classes.onlineDot : classes.offlineDot}`} />
          </IconButton>
        </Tooltip>
      </div>

      {activeContact && (
        <>
          <Chip
            className={classes.activeCallChip}
            icon={<PhoneInTalkIcon />}
            label={`${activeCustomer ? `${activeCustomer.firstName} ${activeCustomer.lastName}` : activeContact.phone} - ${formatDuration(callDuration)}`}
            size="small"
          />
          <CustomerPopup
            contact={activeContact}
            customer={activeCustomer}
            callDuration={callDuration}
          />
        </>
      )}

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem disabled>
          <Typography variant="body2">
            {isConnected ? (
              <span style={{ color: '#4caf50' }}>● Connected</span>
            ) : connectionStatus === 'connecting' ? (
              <span style={{ color: '#ff9800' }}>● Connecting...</span>
            ) : (
              <span style={{ color: '#f44336' }}>● Disconnected</span>
            )}
          </Typography>
        </MenuItem>
        {activeContact && (
          <MenuItem disabled>
            <Typography variant="body2">
              📞 Active Call: {activeContact.phone}
            </Typography>
          </MenuItem>
        )}
        <MenuItem disabled>
          <Typography variant="caption" color="textSecondary">
            Thulium Integration
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

// Customer Popup Component - PhoneIntegration içinde kullanım için
const CustomerPopup = ({ contact, customer, callDuration }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const navigateToOverview = () => {
    const currentPath = history.location?.pathname;
    if (currentPath === '/overview') {
      window.dispatchEvent(new CustomEvent('phoneNavigateToCustomer'));
      return;
    }
    history.push('/overview');
  };

  if (!contact) return null;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Customer bulunduğunda - mevcut müşteriye git
  const handleGoToEmployee = async () => {

    try {
      const rawPhone = (contact?.phone || customer?.phone || '').toString();
      const searchPhone = rawPhone.startsWith('40') ? rawPhone.slice(2) : rawPhone;
      const res = await axios.get('/GetSearchMain/1/' + encodeURIComponent(searchPhone));
      const response = res?.data;
      const firstResult = Array.isArray(response) ? response[0] : null;

      if (firstResult) {
        sessionStorage.removeItem('SearhResult');
        sessionStorage.setItem('SearhResult', JSON.stringify(firstResult));
        sessionStorage.removeItem('ConsumerId');
        sessionStorage.setItem('ConsumerId', firstResult.ConsumerId);
        navigateToOverview();
        return;
      }

      console.warn('[PhoneIntegration] No result from GetSearchMain, using customer fallback');
    } catch (err) {
      console.error('[PhoneIntegration] GetSearchMain failed, using customer fallback:', err);
    }

    if (customer?.id) {
      sessionStorage.removeItem('SearhResult');
      sessionStorage.setItem('SearhResult', JSON.stringify({
        ConsumerId: customer.id,
        FirstName: customer.firstName,
        LastName: customer.lastName,
        MobilePhone: contact?.phone || customer.phone || '',
        Phones: [],
        Addresses: [],
        SearchId: 0,
        WorkCaseCount: 0,
        AssetCount: 0,
        FeedBackCount: 0,
      }));
      sessionStorage.removeItem('ConsumerId');
      sessionStorage.setItem('ConsumerId', customer.id);
      navigateToOverview();
      return;
    }

    console.error('[PhoneIntegration] Could not navigate to customer: no search result and no customer id');
  };

  // Customer bulunamadığında - yeni müşteri oluştur
  const handleCreateEmployee = () => {

    dispatch(communicationActions.setIncomingPhoneNumber(contact.phone));

    const currentPath = history.location?.pathname;
    if (currentPath === '/overview') {
      window.dispatchEvent(new CustomEvent('phoneCreateCustomer', { detail: { phone: contact.phone } }));
      return;
    }
    history.push('/overview');
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.popup}>
        <div style={styles.header}>
          <h3 style={styles.title}>📞 Incoming Call</h3>
          <div style={styles.contactId}>Contact ID: {contact.contactId}</div>
        </div>

        <div style={styles.section}>
          <div style={styles.label}>Calling From:</div>
          <div style={styles.phoneNumber}>{contact.phone}</div>
        </div>

        {customer ? (
          <div style={styles.customerInfo}>
            <div style={styles.customerHeader}>
              <span style={styles.customerIcon}>👤</span>
              <span style={styles.customerName}>{customer.firstName} {customer.lastName}</span>
            </div>

            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Customer ID:</div>
                <div style={styles.detailValue}>{customer.id}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>First Name:</div>
                <div style={styles.detailValue}>{customer.firstName}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Last Name:</div>
                <div style={styles.detailValue}>{customer.lastName}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Phone:</div>
                <div style={styles.detailValue}>{customer.phone}</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.noCustomer}>
            <div style={styles.noCustomerIcon}>❌</div>
            <div style={styles.noCustomerText}>No matching customer found</div>
            <div style={styles.noCustomerSubtext}>Phone: {contact.phone}</div>
          </div>
        )}

        <div style={styles.footer}>
          <div style={styles.timer}>
            <span style={styles.timerIcon}>⏱️</span>
            <span style={styles.timerText}>{formatDuration(callDuration)}</span>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={customer ? handleGoToEmployee : handleCreateEmployee}
            style={styles.overviewButton}
          >
            {customer ? 'Go to the Customer' : 'Create Customer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 9999
  },
  popup: {
    backgroundColor: '#ffffff',
    border: '2px solid #ff9900',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    width: '400px',
    maxHeight: '600px',
    overflow: 'auto'
  },
  header: {
    backgroundColor: '#232f3e',
    color: '#ffffff',
    padding: '16px',
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px'
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600'
  },
  contactId: {
    fontSize: '12px',
    color: '#aaa',
    fontFamily: 'monospace'
  },
  section: {
    padding: '16px',
    borderBottom: '1px solid #e0e0e0'
  },
  label: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
    fontWeight: '500'
  },
  phoneNumber: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#232f3e',
    fontFamily: 'monospace'
  },
  customerInfo: {
    padding: '16px'
  },
  customerHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px'
  },
  customerIcon: {
    fontSize: '24px',
    marginRight: '12px'
  },
  customerName: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#232f3e'
  },
  detailsGrid: {
    display: 'grid',
    gap: '12px'
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0'
  },
  detailLabel: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '500'
  },
  detailValue: {
    fontSize: '13px',
    color: '#232f3e',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '200px',
    wordBreak: 'break-word'
  },
  noCustomer: {
    padding: '32px 16px',
    textAlign: 'center'
  },
  noCustomerIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  noCustomerText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '8px'
  },
  noCustomerSubtext: {
    fontSize: '13px',
    color: '#999',
    fontFamily: 'monospace'
  },
  footer: {
    padding: '12px 16px',
    backgroundColor: '#f5f5f5',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center'
  },
  overviewButton: {
    backgroundColor: '#ff9900',
    color: '#232f3e',
    fontWeight: 'bold',
    textTransform: 'none',
    padding: '8px 24px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: '#ec7211'
    }
  },
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  timerIcon: {
    fontSize: '18px'
  },
  timerText: {
    fontSize: '18px',
    fontWeight: '700',
    fontFamily: 'monospace',
    color: '#232f3e'
  }
};

export default PhoneIntegration;
